<template>
  <div>
    <slot :onNotificationClick="onNotificationClick"> </slot>
  </div>
</template>
<script>
import _ from 'lodash'
import { mapActions } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import {
  NOTIFICATION_ENTITIES,
  NOTIFICATION_TYPES
} from './constants'

export default {
  name: 'NotificationsWrapper',

  mixins: [mixin, trackerMixin],

  methods: {
    ...mapActions(['markNotificationAsRead', 'switchOrganization']),

    async onNotificationClick(notification) {
      const notificationType = this.getNotificationType(notification)

      if (notification._id) {
        await this.markNotificationAsRead({
          notificationId: notification?._id ?? notification?.id,
        })
      }

      switch (notificationType) {
        case NOTIFICATION_TYPES.REV_DELETED:
          return
        case NOTIFICATION_TYPES.COMMENT_CREATED:
          return this.handleCommentNotification(notification)
        case NOTIFICATION_ENTITIES.CHALLENGE:
          return this.navigateToEntityRoute(notification)
        case NOTIFICATION_ENTITIES.FINDING:
          return this.handleFindingNotification(notification)
        case NOTIFICATION_ENTITIES.ROUTINE_DEFINITION:
          return this.handleRoutineAssignNotification(notification)
        case NOTIFICATION_ENTITIES.ROUTINE_EXECUTION:
          return this.handleRoutineExecutionNotification(notification)
        case NOTIFICATION_ENTITIES.RECOGNITION:
          return this.handleRecognitionNotification(notification)
        case NOTIFICATION_TYPES.VALIDATION_REQUEST_ASSIGNED:
          return this.handleValidationRequestAssignedNotification(notification)
        case NOTIFICATION_ENTITIES.REPORT:
          return this.$emit(
            'showReportsDrawer',
            notification?.entities?.[0]?.id
          )
        case NOTIFICATION_TYPES.REV_COMPLETED_WITH_COACH:
          return this.handleRevCompletedWithCoachNotification()
        case NOTIFICATION_TYPES.MEMBER_ADDED_TO_TEAM:
          return this.handleMemberAddedToTeamNotification(notification)
        case NOTIFICATION_ENTITIES.TASK:
          return this.handleTaskNotification(notification)
        case NOTIFICATION_ENTITIES.BOARD:
          return this.handleBoardsNotification(notification)
        case NOTIFICATION_TYPES.TASKS_REMINDER:
          return this.handleTaskReminderNotification(notification)
        case NOTIFICATION_TYPES.ROUTINE_DEFINITION_REMINDER:
          return this.handleRoutineDefinitionReminder(notification)
        case NOTIFICATION_TYPES.REVS_PENDING_TO_VALIDATE_REMINDER:
          return this.handleRevPendingToValidate()
        case NOTIFICATION_TYPES.REVS_ASSIGNED_PENDING_TO_VALIDATE_REMINDER:
          return this.handleAssignedRevsPendingToValidate()
        case NOTIFICATION_TYPES.ROUTINE_COLLABORATOR_ADDED:
          return this.handleRoutineAssignNotification(notification)
        case NOTIFICATION_TYPES.ROUTINE_COLLABORATOR_REMOVED:
          return this.handleRoutineAssignNotification(notification)
        case NOTIFICATION_TYPES.ROUTINE_APPROVAL_REQUESTED:
        case NOTIFICATION_TYPES.ROUTINE_APPROVED:
        case NOTIFICATION_TYPES.ROUTINE_APPROVAL_REJECTED:
          return this.handleRoutineApprovalNotification(notification)
        case NOTIFICATION_TYPES.ROUTINE_APPROVAL_DAILY_REMINDER:
          return this.handleRoutineApprovalReminder(notification)
        case NOTIFICATION_TYPES.REV_REPLICATED:
          return this.navigateToEntityRoute(notification)
        case NOTIFICATION_TYPES.SECONDARY_INVITATION:
          return this.handleSecondaryInvitationNotification(notification)

        default:
          this.trackRevViewEvent(this.getNotificationEntityData(notification))
          this.navigateToEntityRoute(notification)
      }
    },

    trackRevViewEvent(rev) {
      this.trackView(rev)
    },

    getNotificationType(notification) {
      const remindersAndSpecialNotifications = [
        NOTIFICATION_TYPES.REV_DELETED,
        NOTIFICATION_TYPES.COMMENT_CREATED,
        NOTIFICATION_TYPES.VALIDATION_REQUEST_ASSIGNED,
        NOTIFICATION_TYPES.TASKS_REMINDER,
        NOTIFICATION_TYPES.ROUTINE_DEFINITION_REMINDER,
        NOTIFICATION_TYPES.REVS_PENDING_TO_VALIDATE_REMINDER,
        NOTIFICATION_TYPES.REVS_ASSIGNED_PENDING_TO_VALIDATE_REMINDER,
        NOTIFICATION_TYPES.ROUTINE_APPROVAL_REQUESTED,
        NOTIFICATION_TYPES.ROUTINE_APPROVED,
        NOTIFICATION_TYPES.ROUTINE_APPROVAL_REJECTED,
        NOTIFICATION_TYPES.ROUTINE_APPROVAL_DAILY_REMINDER,
        NOTIFICATION_TYPES.SECONDARY_INVITATION,
      ]

      if (remindersAndSpecialNotifications.includes(notification.type)) {
        return notification.type
      } else {
        return this.getNotificationEntityType(notification)
      }
    },

    getNotificationEntityType(notification) {
      const notificationEntity = notification?.entities?.[0]
      const metadata = notification?.metadata

      if (!_.isEmpty(notificationEntity)) {
        const entityType = notificationEntity?.type

        return entityType === NOTIFICATION_ENTITIES.IMPROVEMENT_IDEA
          ? NOTIFICATION_ENTITIES.REV
          : entityType
      }

      if (metadata?.likedEntityType) return metadata.likedEntityType
      if (metadata?.revType) return metadata.revType
      if (metadata?.rev) return NOTIFICATION_ENTITIES.REV
      if (metadata?.problemSolving) {
        return NOTIFICATION_ENTITIES.PROBLEM_SOLVING
      }
      if (metadata?.challenge) return NOTIFICATION_ENTITIES.CHALLENGE
    },

    getNotificationEntityData(notification) {
      if (!_.isEmpty(notification?.entities?.[0])) {
        return notification?.entities?.[0]
      }

      const metadata = notification?.metadata
      return metadata?.rev ?? metadata?.problemSolving ?? metadata?.challenge
    },

    getNotificationEntityId(notification) {
      const entity = this.getNotificationEntityData(notification)
      return entity?._id || entity?.id
    },

    navigateToEntityRoute(notification, routeQuery) {
      const organizationId = this.getOrganizationId()
      const entityTypeInKebabCase = _.kebabCase(
        this.getNotificationEntityType(notification)
      )
      const entityId = this.getNotificationEntityId(notification)

      if (!entityId || !entityTypeInKebabCase) return

      return this.navigateTo({
        path: `/${entityTypeInKebabCase}/${organizationId}/${entityId}`,
        query: routeQuery,
      })
    },

    handleCommentNotification(notification) {
      const commentData = notification?.entities?.find?.(
        entity => entity.type === NOTIFICATION_ENTITIES.COMMENT
      )
      const isTask = notification?.entities?.some?.(
        entity => entity.type === NOTIFICATION_ENTITIES.TASK
      )
      if (!commentData) return this.navigateToEntityRoute(notification)
      if (isTask) return this.handleTaskNotification(notification)

      this.navigateToEntityRoute(notification, {
        highlightedComment: commentData.id,
      })
    },

    handleFindingNotification(notification) {
      const finding = notification?.metadata?.finding
      if (!finding) return

      return this.navigateTo({
        name: 'routineExecutionDetailFinding',
        params: {
          routineId: finding?.routine?.id ?? finding?.routine,
          findingId: finding?.id,
        },
      })
    },

    handleRoutineExecutionNotification(notification) {
      const routineId = notification.entities[0].id
      if (!routineId) return

      return this.navigateTo({ path: `/routine-executions/${routineId}` })
    },

    handleRoutineApprovalNotification(notification) {
      const routineId = notification.entities[0].id
      if (!routineId) return

      return this.navigateTo({
        path: `/routine-executions/${routineId}?tab=approvals`,
      })
    },

    handleRoutineApprovalReminder(notification) {
      const accountId =
        notification?.metadata?.deeplink?.query?.waitingForMyApproval?.[0]
      if (!accountId) return

      return this.navigateTo({
        name: 'explore',
        query: {
          waitingForMyApproval: accountId,
          page: 1,
        },
      })
    },

    handleRecognitionNotification(notification) {
      const recognitionId = notification.entities[0].id
      if (!recognitionId) return
      return this.navigateTo({
        name: 'recognitions',
        query: {
          highlightedRecognition: recognitionId,
        },
      })
    },
    handleMemberAddedToTeamNotification(notification) {
      const teamId = notification.entities[0].id
      if (!teamId) return
      return this.navigateTo({
        name: 'teamDetails',
        params: { teamId },
      })
    },

    handleValidationRequestAssignedNotification(notification) {
      const revId = notification.entities[0].id
      const revType = notification.entities[0].type
      if (!revId || !revType) return

      return this.navigateTo({
        name: 'validateRev',
        params: {
          revId: revId,
          revType: _.kebabCase(revType),
        },
      })
    },

    handleRoutineAssignNotification(notification) {
      const routineId = notification.entities[0].id
      if (!routineId) return

      return this.navigateTo({ path: `/routine-definitions/${routineId}` })
    },

    handleRevCompletedWithCoachNotification() {
      return this.navigateTo({
        name: 'validations',
        query: {
          activeTab: 0,
          filter: 'pending',
          role: 'coach',
        },
      })
    },

    handleTaskNotification(notification) {
      const taskId = notification.entities[0].id
      if (!taskId) return
      const userIsInTasksView = this.$route.name === 'tasks'
      const initialQueryParams = this.$route.query
      const query = userIsInTasksView
        ? { ...initialQueryParams, task: taskId }
        : { task: taskId }

      return this.navigateTo({
        name: 'tasks',
        query,
      })
    },

    handleBoardsNotification(notification) {
      const boardId = notification.entities[0].id
      if (!boardId) return

      return this.navigateTo({ path: `/boards/${boardId}` })
    },

    handleTaskReminderNotification() {
      return this.navigateTo({
        path: `/tasks?status=open&status=inProgress&assignees=${this.$store.getters.accountId}&type=assignedToMe&group=dueDate&sort=dueDate-asc`,
      })
    },
    handleRoutineDefinitionReminder(notification) {
      const routineId = notification.entities[0].id
      if (!routineId) return

      return this.navigateTo({
        path: `/routine-definitions/${routineId}/start`,
      })
    },

    handleRevPendingToValidate() {
      return this.navigateTo({
        name: 'validations',
        query: {
          activeTab: 1,
          filter: 'pending',
        },
      })
    },
    handleAssignedRevsPendingToValidate() {
      return this.navigateTo({
        name: 'validations',
        query: {
          activeTab: 1,
          filter: 'assignedToMe',
        },
      })
    },

    async handleSecondaryInvitationNotification(notification) {
      const organizationId = notification?.entities?.[0]?.organization
      if (!organizationId) return

      try {
        await this.switchOrganization({ organizationId, shouldJoin: true })
      } catch (error) {
        this.showError(
          this.$t('organization_settings.switch_organization.error')
        )
      }
    },
  },
}
</script>
